"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Search, Settings, FileCheck, TrendingUp, Award, ArrowRight, CircleCheck as CheckCircle2, CalendarCheck, ShieldCheck, ChartBar as BarChart3, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const roadmapPhases = [{
  phase: "Phase 01",
  title: "Local Market & Technical Audit",
  timeframe: "Weeks 1 - 2",
  icon: Search,
  summary: "A forensic analysis of your Google Business Profile, local NAP consistency, and geo-targeted ranking footprint.",
  deliverables: ["Google Business Profile (GBP) deep audit and category analysis", "Local citation audit across UK core directories and aggregators", "NAP (Name, Address, Phone) consistency scan", "Technical geo-tagging, schema markup, and mobile index audit"]
}, {
  phase: "Phase 02",
  title: "GBP & Local Foundation Setup",
  timeframe: "Weeks 3 - 4",
  icon: MapPin,
  summary: "Fixing core citations, optimizing your Google map listing, and building geo-specific structured data directly.",
  deliverables: ["Complete Google Business Profile optimization (products, services, attributes)", "Structured LocalBusiness schema implementation with precise coordinates", "Cleanup and reconciliation of conflicting or duplicate citations", "Primary secondary category refinement for targeted map pack visibility"]
}, {
  phase: "Phase 03",
  title: "Localised Landing Pages & Content",
  timeframe: "Months 2 - 3",
  icon: FileCheck,
  summary: "Creating high-intent city, county, and service area pages designed to capture organic search and convert enquiries.",
  deliverables: ["Dedicated location page architecture built for target towns and cities", "Locally relevant content creation addressing specific service search intent", "Internal linking mesh connecting location hubs with core service offerings", "Geo-targeted on-page metadata, headers, and media alt-tag optimisation"]
}, {
  phase: "Phase 04",
  title: "Local Authority & Map Pack Velocity",
  timeframe: "Ongoing (Months 4+)",
  icon: TrendingUp,
  summary: "Building sustainable local backlinks, managing review velocity strategies, and monitoring competitive map grid movements.",
  deliverables: ["Targeted local link acquisition from regional business bodies and news outlets", "Strategic review generation framework and customer sentiment response setup", "Monthly geo-grid rank tracking and local conversion rate monitoring", "Direct monthly strategy review calls directly with me"]
}];
const guarantees = [{
  title: "Direct Implementation",
  description: "Hands-on execution of technical fixes, profile optimizations, and location page rollouts across every phase.",
  icon: ShieldCheck
}, {
  title: "Zero Artificial Tactics",
  description: "Strictly compliant white-hat local SEO practices that build long-term value.",
  icon: Award
}, {
  title: "Clear Monthly Reporting",
  description: "Live map grid rankings, organic calls, and local enquiry metrics every month.",
  icon: BarChart3
}];
export default function Roadmap() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState(0);
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoexecutionroadmap" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wide">
            Execution Framework
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground">
            My Local SEO Execution Roadmap
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            I don&apos;t use generic checklists. This is my step-by-step roadmap designed to dominate Google Map Packs and secure top local organic rankings for your business locations.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-4 gap-6 relative">
          {roadmapPhases.map((phase, idx) => {
          const IconComponent = phase.icon;
          const isSelected = activeTab === idx;
          return <motion.div key={phase.phase} variants={itemVariants} data-index={idx} className="flex flex-col h-full">
                <Card onClick={() => setActiveTab(idx)} className={`h-full border transition-all cursor-pointer flex flex-col justify-between ${isSelected ? "border-primary shadow-lg bg-card ring-1 ring-primary/20" : "border-border shadow-xs hover:border-primary/40 bg-card/60"}`}>
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary font-mono tracking-wider">
                        {phase.phase}
                      </span>
                      <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-md font-medium">
                        {phase.timeframe}
                      </span>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-lg w-fit">
                      <IconComponent className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-bold font-serif">
                      {phase.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      {phase.summary}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <div className="border-t border-border/80 pt-4">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        Key Deliverables:
                      </p>
                      <ul className="space-y-2">
                        {phase.deliverables.map((item, dIdx) => <li key={dIdx} data-index={dIdx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <Badge variant="outline" className="text-xs font-medium border-border">
                Consultant Standard
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                How I Deliver Measurable Local Impact
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                When you partner with me, you get a dedicated specialist who audits the data, writes the code, and builds the local authority personally.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {guarantees.map((item, gIdx) => {
              const GuaranteeIcon = item.icon;
              return <div key={gIdx} data-index={gIdx} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border shadow-xs">
                    <div className="p-2.5 bg-primary/10 text-primary rounded-lg shrink-0 mt-0.5">
                      <GuaranteeIcon className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-base text-card-foreground">
                        {item.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7 bg-card border border-border p-6 sm:p-8 rounded-2xl shadow-sm">
            <h4 className="text-lg font-bold font-serif text-foreground mb-4 flex items-center gap-2">
              <Building2 className="size-5 text-primary" />
              Frequently Asked Execution Questions
            </h4>
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              <AccordionItem value="item-1" data-index={0}>
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline">
                  How long does it typically take to rank in Google&apos;s 3-Pack?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                  While every market is different, most clients begin noticing improvements in map positioning and discovery calls within 60 to 90 days after resolving NAP discrepancies, updating primary Google Business categories, and deploying location-specific landing pages.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" data-index={1}>
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline">
                  Do you handle multi-location or national regional businesses?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                  Yes. I structure scalable local architectures featuring distinct landing pages, unique Google Business Profiles, localized schema markup, and dedicated citation portfolios for each geographical operational hub.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" data-index={2}>
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline">
                  Will I need to write the content or update the website myself?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                  No. I handle the technical implementation, structured data generation, and localized copy creation directly. If your technical setup requires developer sign-off, I supply detailed, developer-ready implementation briefs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" data-index={3}>
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline">
                  How do we track success and phone enquiries?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                  We configure Google Business Profile tracking with UTM tags and monitor local phone calls, direction requests, website visits, and localized keyword map grids every single month.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <div className="mt-16 sm:mt-20 p-8 sm:p-12 rounded-2xl bg-secondary border border-border shadow-md flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-secondary-foreground">
              Ready to claim your local territory?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Book a direct consultation with me to review your local search visibility and receive a custom execution plan.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto shadow-md">
              <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                <CalendarCheck className="size-4" />
                <span>Book a Free SEO Consultation</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage" className="flex items-center justify-center gap-2">
                <span>View Case Studies</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}